/* The desk notes row: stickies above Next availability.
 *
 * Staff leave each other notes here and the ClassPass worker leaves ones for the
 * desk ("booking failed, call her"). Which notes show, and when they end, is
 * js/rules/notes.js and the store; this is only the drawing and the two small
 * forms, Dismiss and + Note.
 *
 * Note text is interpolated with {{ }} and nothing else. Some of it begins life
 * in an email, so it must never be parsed as markup. test: desk-notes.test.js.
 *
 * With no notes only the + Note button takes up room, so the board is where it
 * always was on a quiet day. With notes, a bar above them says how many are
 * open and collapses the row (Kevin, 2026-11-02). It opens by default, and opens
 * again by itself when a note arrives or someone starts one. + Note sits after
 * the last note; while the row is collapsed, and on a phone where it would be a
 * line of its own, it sits in the bar.
 */
import * as Vue from 'vue';
import { store } from '../store.js';
import { timeLeft, sourceName, hasNewNote } from '../rules/notes.js';

var COLORS = ['yellow', 'blue', 'green', 'pink'];
var NOTE_MAX = 500;

export const DeskNotes = {
  name: 'DeskNotes',
  setup: function () {
    // Per note, per screen: which ones are asking "Dismiss this note?".
    var confirming = Vue.reactive({});

    var form = Vue.reactive({
      open: false, body: '', color: 'yellow', author: '', urgent: false,
      choice: 'today', picked: '', error: ''
    });

    // Per screen and not remembered: a reload shows the notes again.
    var expanded = Vue.ref(true);
    var seen = new Set(store.deskNotes.value.map(function (n) { return n.id; }));
    Vue.watch(store.deskNotes, function (rows) {
      if (hasNewNote(seen, rows)) expanded.value = true;
      // Nothing left to hide: the next note should not arrive into a shut row.
      if (!rows.length) expanded.value = true;
      rows.forEach(function (n) { seen.add(n.id); });
    });

    var anyUrgent = Vue.computed(function () {
      return store.deskNotes.value.some(function (n) { return n.urgent; });
    });
    var addInBar = Vue.computed(function () {
      return store.deskNotes.value.length > 0 && (!expanded.value || store.ui.phone);
    });

    function startNote() {
      expanded.value = true;
      form.open = true;
    }

    function resetForm() {
      form.body = ''; form.color = 'yellow'; form.author = ''; form.urgent = false;
      form.choice = 'today'; form.picked = ''; form.error = '';
    }

    function meta(n) {
      var now = store.minuteNow.value;
      var who = n.source && n.source !== 'desk' ? sourceName(n.source) : (n.author || '');
      var end = new Date(n.expiresAt);
      var sameDay = end.toDateString() === new Date(now).toDateString();
      // A weekday is ambiguous a week out, so past 6 days the end is a date.
      var far = n.expiresAt - now > 6 * 86400000;
      var ends = 'ends ' + (sameDay
        ? store.formatClock(n.expiresAt)
        : (far ? end.toLocaleDateString([], { month: 'short', day: 'numeric' })
               : end.toLocaleDateString([], { weekday: 'short' })) + ' ' + store.formatClock(n.expiresAt));
      var left = n.createdAt ? 'left ' + store.formatClock(n.createdAt) : '';
      return [who, left, timeLeft(n.expiresAt - now), ends].filter(Boolean).join(' · ');
    }

    function ask(n) { confirming[n.id] = true; }
    function keep(n) { delete confirming[n.id]; }
    function dismiss(n) {
      delete confirming[n.id];
      store.dismissNote(n.id);
    }

    // The next time the clock reads HH:MM: today if that is still ahead,
    // otherwise tomorrow. Null when nothing was picked.
    function pickedMs() {
      var m = /^(\d{1,2}):(\d{2})$/.exec(form.picked || '');
      if (!m) return null;
      var d = new Date();
      d.setHours(Number(m[1]), Number(m[2]), 0, 0);
      if (d.getTime() <= Date.now()) d.setDate(d.getDate() + 1);
      return d.getTime();
    }

    function post() {
      var ok = store.postNote({
        body: form.body, color: form.color, urgent: form.urgent,
        author: form.author, choice: form.choice, pickedMs: pickedMs()
      });
      if (!ok) {
        form.error = !form.body.trim() ? 'Write the note first.'
          : form.choice === 'pick' ? 'Pick a time for it to end.'
          : 'That note could not be posted.';
        return;
      }
      resetForm();
      form.open = false;
    }

    function cancel() { resetForm(); form.open = false; }

    return {
      notes: store.deskNotes,
      ui: store.ui,
      confirming: confirming,
      form: form,
      expanded: expanded,
      anyUrgent: anyUrgent,
      addInBar: addInBar,
      startNote: startNote,
      colors: COLORS,
      noteMax: NOTE_MAX,
      meta: meta,
      ask: ask,
      keep: keep,
      dismiss: dismiss,
      post: post,
      cancel: cancel
    };
  },
  template: [
    '<section class="desk-notes" :class="{ \'desk-notes--phone\': ui.phone }" aria-label="Desk notes">',
    '  <div v-if="notes.length" class="desk-notes__bar">',
    '    <button type="button" class="desk-notes__toggle" :aria-expanded="expanded ? \'true\' : \'false\'"',
    '      aria-controls="desk-notes-list" :title="expanded ? \'Hide notes\' : \'Show notes\'" @click="expanded = !expanded">',
    '      <span class="desk-notes__chev" aria-hidden="true"></span>',
    '      <span class="desk-notes__count" :class="{ \'desk-notes__count--urgent\': anyUrgent }">{{ notes.length }}</span>',
    '      <span>{{ notes.length === 1 ? \'open note\' : \'open notes\' }}</span>',
    '    </button>',
    '    <button v-if="addInBar && !form.open" type="button" class="desk-notes__add" @click="startNote">+ Note</button>',
    '  </div>',
    '  <div v-if="expanded || !notes.length" id="desk-notes-list" class="desk-notes__list">',
    '    <div v-for="n in notes" :key="n.id" class="desk-note"',
    '         :class="[\'desk-note--\' + n.color, { \'desk-note--urgent\': n.urgent }]">',
    '      <div class="desk-note__text">{{ n.body }}</div>',
    '      <div class="desk-note__foot">',
    '        <template v-if="confirming[n.id]">',
    '          <b class="desk-note__ask">Dismiss this note?</b>',
    '          <span class="desk-note__btns">',
    '            <button type="button" class="desk-note__btn desk-note__btn--solid" @click="dismiss(n)">Yes, dismiss</button>',
    '            <button type="button" class="desk-note__btn" @click="keep(n)">Keep</button>',
    '          </span>',
    '        </template>',
    '        <template v-else>',
    '          <span class="desk-note__meta">{{ meta(n) }}</span>',
    '          <button type="button" class="desk-note__btn" @click="ask(n)">Dismiss</button>',
    '        </template>',
    '      </div>',
    '    </div>',
    '  <form v-if="form.open" class="desk-note desk-note--form" :class="\'desk-note--\' + form.color" @submit.prevent="post">',
    '    <textarea v-model="form.body" class="desk-note__input" rows="2" :maxlength="noteMax"',
    '      placeholder="What should the desk know?" aria-label="Note"></textarea>',
    '    <div class="desk-note__swatches" role="group" aria-label="Colour">',
    '      <button v-for="c in colors" :key="c" type="button" class="desk-note__swatch"',
    '        :class="\'desk-note__swatch--\' + c" :aria-pressed="form.color === c"',
    '        :aria-label="c" @click="form.color = c"></button>',
    '      <label class="desk-note__check"><input type="checkbox" v-model="form.urgent"> Flash as urgent</label>',
    '    </div>',
    '    <input v-model="form.author" class="desk-note__input desk-note__input--name" type="text"',
    '      maxlength="40" placeholder="Your name (optional)" aria-label="Your name">',
    '    <div class="desk-note__ends" role="group" aria-label="Ends">',
    '      <span class="desk-note__meta">Ends:</span>',
    '      <label><input type="radio" value="today" v-model="form.choice"> Today</label>',
    '      <label><input type="radio" value="tomorrow" v-model="form.choice"> Tomorrow</label>',
    '      <label><input type="radio" value="pick" v-model="form.choice"> Pick a time</label>',
    '      <input v-if="form.choice === \'pick\'" v-model="form.picked" type="time" class="desk-note__time" aria-label="End time">',
    '    </div>',
    '    <p v-if="form.error" class="desk-note__error" role="alert">{{ form.error }}</p>',
    '    <div class="desk-note__foot">',
    '      <button type="button" class="desk-note__btn" @click="cancel">Cancel</button>',
    '      <button type="submit" class="desk-note__btn desk-note__btn--solid">Post</button>',
    '    </div>',
    '  </form>',
    '  <button v-else-if="!addInBar" type="button" class="desk-notes__add" @click="startNote">+ Note</button>',
    '  </div>',
    '</section>'
  ].join('\n')
};
